import { FacetType, FilterChip, RangeFacet, ValueFacet } from '@/src/lib/category/types.ts';
import { ReadonlyURLSearchParams } from 'next/dist/client/components/navigation';
import { Dictionary } from '@/src/lib/dictionary/types.ts';
import { DeserializedObject } from '@/src/lib/http/jsonapi/deserializer.ts';
import { DeserializedCatalogSearch } from '@/src/lib/catalog-search/types.ts';

export function findFacetByName(facets: Array<ValueFacet | RangeFacet>, name: string) {
  return facets.find((facet) => facet.name === name);
}

export function getFacetType(facet: ValueFacet | RangeFacet) {
  if ('values' in facet) return FacetType.VALUES;
  if ('min' in facet) return FacetType.RANGE;

  return undefined;
}

function sortFacetsBySearchParams(
  facets: Array<ValueFacet | RangeFacet>,
  searchParams: ReadonlyURLSearchParams | null,
  reverse: boolean = false,
) {
  const searchParamsKeys = (searchParams ? Array.from(searchParams.keys()) : []).map((key) =>
    key.replace(/\[.*\]/g, ''),
  );

  return [...facets].sort((facetA, facetB) => {
    const facetAIndex = searchParamsKeys.indexOf(facetA.name);
    const facetBIndex = searchParamsKeys.indexOf(facetB.name);
    let sortingScore = 1;

    if (facetAIndex === facetBIndex) sortingScore = 0;
    if (facetAIndex > facetBIndex) sortingScore = -1;
    if (reverse) sortingScore *= -1;

    return sortingScore;
  });
}

export function prepareFacets(facets: Array<ValueFacet | RangeFacet>) {
  const blockList = ['is_available', 'category'];

  return facets
    .filter((facet) => !blockList.includes(facet.name))
    .map((facet) => {
      if (getFacetType(facet) === FacetType.VALUES) {
        facet.isDisabled = (facet as ValueFacet).values.length === 0;
      }

      if (getFacetType(facet) === FacetType.RANGE) {
        facet = facet as RangeFacet;
        // disable range facets if there is only a single possible value
        facet.isDisabled = facet.min === facet.max;
      }

      return facet;
    });
}

export function buildValueFacetChips(facet: ValueFacet) {
  if (Array.isArray(facet.activeValue)) {
    return facet.activeValue.map((activeValue) => ({
      params: [
        {
          name: `${facet.name}[]`,
          value: activeValue,
        },
      ],
      label: `${facet.localizedName}: ${activeValue}`,
    }));
  }

  return facet.activeValue
    ? [
        {
          params: [{ name: facet.name, value: facet.activeValue }],
          label: `${facet.localizedName}: ${facet.activeValue}`,
        },
      ]
    : [];
}

export function buildRangeFacetChips(facet: RangeFacet, dict: Dictionary) {
  if (facet.min !== facet.activeMin || facet.max !== facet.activeMax) {
    return [
      {
        params: [
          {
            name: `${facet.name}[min]`,
            value: facet.activeMin.toString(),
          },
          {
            name: `${facet.name}[max]`,
            value: facet.activeMax.toString(),
          },
        ],
        label: `${facet.localizedName}: ${facet.activeMin} ${dict.category.filters.rangeTo} ${facet.activeMax}`,
      },
    ];
  }

  return [];
}

export function buildFilterChips(
  facets: Array<ValueFacet | RangeFacet>,
  searchParams: ReadonlyURLSearchParams | null,
  dict: Dictionary,
): Array<FilterChip> {
  return sortFacetsBySearchParams(facets, searchParams, true)
    .map((facet) => {
      if (getFacetType(facet) === FacetType.VALUES) {
        return buildValueFacetChips(facet as ValueFacet);
      }

      if (getFacetType(facet) === FacetType.RANGE) {
        return buildRangeFacetChips(facet as RangeFacet, dict);
      }

      return [];
    })
    .flat();
}

export function mergeFacetsFromCatalogSearch(
  catalogSearchData: DeserializedObject<DeserializedCatalogSearch>,
): Array<ValueFacet | RangeFacet> {
  return [...catalogSearchData.valueFacets, ...catalogSearchData.rangeFacets];
}
